import { STANDARD_PIPELINE_TYPE } from "@/marketing/models/campaignPipeline";
import {
  CampaignLimitationKind,
  type AffiliateCampaignTerms,
  type CampaignBriefStructured,
  type CampaignLimitation,
  type CreateInfluencerCampaignRequest,
  type InfluencerCampaignStatus,
  type InfluencerCampaignTrafficLight,
  type ProductAllocation,
  type ProductGroup,
  type UpdateInfluencerCampaignRequest,
} from "@/marketing/models/influencerCampaign";

import { type BuiltCardFields } from "./cardFieldsForm";

export interface CampaignFormFieldsState {
  name: string;
  brandId: string;
  trafficLight: InfluencerCampaignTrafficLight | "";
  onboardingTarget: string;
  postTarget: string;
  postingDueDate: string;
  startAt: string;
  endAt: string;
  brief: string;
  productRefs: string[];
  productAllocations: ProductAllocation[];
  productGroups?: ProductGroup[] | undefined;
  limitationRegions: string[];
  // Undefined leaves the terms out of the request; null (update only) clears the stored terms.
  affiliateTerms?: AffiliateCampaignTerms | null | undefined;
}

function rewardForProductConfiguration(
  cardFields: BuiltCardFields,
  isGroupedProductConfiguration: boolean,
): BuiltCardFields["reward"] {
  if (!cardFields.reward || !isGroupedProductConfiguration) {
    return cardFields.reward;
  }

  const groupedReward = { ...cardFields.reward };
  delete groupedReward.productChoiceCount;
  return groupedReward;
}

function normalizedProductGroups(productGroups: ProductGroup[]): ProductGroup[] {
  return productGroups.map((productGroup) => {
    const name = productGroup.name?.trim();
    return {
      groupId: productGroup.groupId,
      choiceCount: productGroup.choiceCount,
      ...(name ? { name } : {}),
    };
  });
}

function toIsoMidday(dateStr: string): string | undefined {
  const trimmed = dateStr.trim();
  if (!trimmed) return undefined;
  const d = new Date(`${trimmed}T12:00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

function parseNonNegativeInt(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isInteger(n) && n >= 0 ? n : undefined;
}

function buildSharedFields(
  state: CampaignFormFieldsState,
  briefStructured: CampaignBriefStructured | undefined,
  cardFields: BuiltCardFields,
): Partial<CreateInfluencerCampaignRequest> {
  const fields: Partial<CreateInfluencerCampaignRequest> = {};
  const onboarding = parseNonNegativeInt(state.onboardingTarget);
  if (onboarding !== undefined) fields.onboardingTarget = onboarding;
  const post = parseNonNegativeInt(state.postTarget);
  if (post !== undefined) fields.postTarget = post;
  const due = toIsoMidday(state.postingDueDate);
  if (due) fields.postingDueDate = due;
  const start = toIsoMidday(state.startAt);
  if (start) fields.startAt = start;
  const end = toIsoMidday(state.endAt);
  if (end) fields.endAt = end;
  if (state.trafficLight) fields.trafficLight = state.trafficLight;
  const trimmedBrief = state.brief.trim();
  if (trimmedBrief) fields.brief = trimmedBrief;
  if (briefStructured !== undefined) fields.briefStructured = briefStructured;
  if (cardFields.types !== undefined) fields.types = cardFields.types;
  if (cardFields.typeConfig !== undefined) fields.typeConfig = cardFields.typeConfig;
  if (cardFields.platforms !== undefined) fields.platforms = cardFields.platforms;
  if (cardFields.tagline !== undefined) fields.tagline = cardFields.tagline;
  if (cardFields.reward !== undefined) {
    const reward = rewardForProductConfiguration(
      cardFields,
      (state.productGroups?.length ?? 0) > 0,
    );
    if (reward !== undefined) fields.reward = reward;
  }
  fields.productRefs = state.productRefs;
  fields.productAllocations = state.productAllocations;
  if (state.productGroups !== undefined) {
    fields.productGroups = normalizedProductGroups(state.productGroups);
  }
  const limitations: CampaignLimitation[] = [];
  if (state.limitationRegions.length > 0) {
    limitations.push({
      kind: CampaignLimitationKind.Region,
      values: state.limitationRegions,
    });
  }
  fields.limitations = limitations;
  return fields;
}

export function buildCreateCampaignRequest(
  state: CampaignFormFieldsState,
  status: InfluencerCampaignStatus,
  ownerId: string,
  briefStructured: CampaignBriefStructured | undefined,
  cardFields: BuiltCardFields,
  pipelineType: string = STANDARD_PIPELINE_TYPE,
): CreateInfluencerCampaignRequest {
  return {
    name: state.name.trim(),
    brandId: state.brandId,
    status,
    ownerId,
    // Absent means standard; omitting it keeps the payload valid on facades without pipelineType.
    ...(pipelineType !== STANDARD_PIPELINE_TYPE && { pipelineType }),
    ...buildSharedFields(state, briefStructured, cardFields),
    ...(state.affiliateTerms && { affiliateTerms: state.affiliateTerms }),
  };
}

export function buildUpdateCampaignRequest(
  state: CampaignFormFieldsState,
  briefStructured: CampaignBriefStructured | undefined,
  cardFields: BuiltCardFields,
): UpdateInfluencerCampaignRequest {
  return {
    name: state.name.trim(),
    brandId: state.brandId,
    ...buildSharedFields(state, briefStructured, cardFields),
    ...(state.affiliateTerms !== undefined && { affiliateTerms: state.affiliateTerms }),
  };
}
